import { z } from "zod";
import { GENDER_VALUES } from "@typings/entities/individuals";
import {
  REGISTER_TYPES,
  REGISTER_ENVIRONMENTS,
} from "@typings/entities/registers/enums";
import {
  validateLatitude,
  validateLongitude,
  validatePositiveInteger,
} from "@utils/formatters";
import { config } from "@/config";
import { ComboBoxDefault } from "@typings/components/comboBox";
import type { DefaultValues } from "react-hook-form";

export const registerSchema = z
  .object({
    reporter: z.object({
      label: z.string().min(1, "Agente designado é obrigatório"),
      value: z.string().min(1, "Agente designado é obrigatório"),
    }),
    individual: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    anonymousIndividualTaxonomicGroup: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    anonymousIndividualGender: z.preprocess(
      (val) => (val === "" ? null : val),
      z
        .enum(GENDER_VALUES, {
          error: "Gênero deve ser um valor válido",
        })
        .nullable()
        .optional(),
    ),

    anonymousGroupMembersNumber: z
      .string()
      .refine(validatePositiveInteger(), {
        message: "Número de membros do grupo deve ser um inteiro positivo",
      })
      .nullable()
      .optional(),
    latitude: z
      .string({ error: "Latitude é obrigatória" })
      .refine(validateLatitude(), {
        message: "Latitude deve estar entre -90 e 90",
      }),
    longitude: z
      .string({ error: "Longitude é obrigatória" })
      .refine(validateLongitude(), {
        message: "Longitude deve estar entre -180 e 180",
      }),
    type: z.enum(REGISTER_TYPES, {
      error: "Tipo de registro deve ser um valor válido",
    }),
    registerDate: z.string().optional(),
    environment: z.preprocess(
      (val) => (val === "" ? null : val),
      z
        .enum(REGISTER_ENVIRONMENTS, {
          error: "Ambiente do registro deve ser um valor válido",
        })
        .nullable()
        .optional(),
    ),
    observations: z.string().optional(),
    sampleCollected: z.boolean(),
    attachments: z
      .array(z.instanceof(File))
      .max(
        config.uploadArchive.maxFiles,
        `Você pode enviar no máximo ${config.uploadArchive.maxFiles} arquivos.`,
      )
      .optional(),
  })
  .refine(
    (data) => {
      const hasIndividual = !!data.individual?.value;
      const hasAnonymous = !!(
        data.anonymousIndividualTaxonomicGroup?.value ||
        data.anonymousIndividualGender ||
        data.anonymousGroupMembersNumber
      );

      return !(hasIndividual && hasAnonymous);
    },
    {
      message:
        "Não é possível preencher tanto o indivíduo quanto os dados anônimos ao mesmo tempo",
      path: ["individual"],
    },
  );

export type RegisterFormData = z.infer<typeof registerSchema>;

export const registerDefaultValues: DefaultValues<RegisterFormData> = {
  reporter: ComboBoxDefault,
  individual: ComboBoxDefault,
  anonymousIndividualTaxonomicGroup: ComboBoxDefault,
  anonymousIndividualGender: undefined,
  anonymousGroupMembersNumber: undefined,
  latitude: undefined,
  longitude: undefined,
  type: undefined,
  registerDate: undefined,
  environment: undefined,
  observations: "",
  sampleCollected: false,
};
